/**
 * Check out guard — one click, one checkout.
 *
 * The cart's Check out button (snippets/cart-summary.liquid, drawer and cart
 * page) is a plain form submit that redirects to Shopify checkout, which can
 * take a second or two. Without a guard, repeat clicks send repeat requests
 * and nothing shows that anything is happening.
 *
 * On the first submit the button shows the shared spinner (aria-busy, styles
 * in assets/custom-body.css), later submits are ignored, and the wallet
 * buttons beside it (PayPal…) are made inert so a second payment can't be
 * started in parallel. It unlocks when the browser restores the page from its
 * back/forward cache (shopper pressed Back on checkout) or after a safety
 * timeout if the navigation never happens.
 *
 * Runs before Horizon's handlers: submit is caught on `window`, capture phase.
 */

const MAX_LOCK_MS = 15000;

/** @type {(() => void) | null} */
let unlock = null;

window.addEventListener(
  'submit',
  (event) => {
    const button = /** @type {SubmitEvent} */ (event).submitter;
    if (!(button instanceof HTMLButtonElement) || button.name !== 'checkout') return;

    if (unlock) {
      // Already on the way to checkout
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Not `disabled`: a disabled submitter is left out of the submission, and
    // the `checkout` field is what sends the shopper to checkout
    button.setAttribute('aria-busy', 'true');
    const wallets = button.closest('.cart__ctas')?.querySelector('.additional-checkout-buttons');
    wallets?.setAttribute('inert', '');

    const timer = setTimeout(() => unlock?.(), MAX_LOCK_MS);
    unlock = () => {
      clearTimeout(timer);
      button.removeAttribute('aria-busy');
      wallets?.removeAttribute('inert');
      unlock = null;
    };
  },
  true
);

window.addEventListener('pageshow', () => unlock?.());
